import React, { useEffect, useState } from 'react'
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Box,
  Typography,
  Chip,
} from '@mui/material'
import {
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material'
import useMediaManager from 'components/common/MediaManager'
import { useSnackbar } from 'notistack'
import { createExpert, updateExpert, getExpertDetail } from 'services/expert'
import { getSimpleProgramList, queryInnerciseByTitle, getPrograms } from 'services/program'
import { StyledExpertDialog } from './Expert.style'

const defaultValues = {
  id: null,
  name: '',
  description: '',
  imageUrl: '',
  imageResourceId: null,
  link: '',
  isActive: true,
  sortOrder: 0,
  socialHandles: {
    x: '',
    instagram: '',
    facebook: '',
    linkedin: '',
    youtube: '',
    tiktok: '',
  },
  programIds: [],
  innerciseIds: [],
}

const useExpertDialog = ({ onSubmit }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [values, setValues] = useState(defaultValues)
  const [programOptions, setProgramOptions] = useState([])
  const [programSearch, setProgramSearch] = useState('')
  const [programResults, setProgramResults] = useState([])
  const [innerciseSearch, setInnerciseSearch] = useState('')
  const [innerciseResults, setInnerciseResults] = useState([])

  const [openMediaManager, MediaManagerContent] = useMediaManager()

  useEffect(() => {
    // Load programs for multi-select
    const loadPrograms = async () => {
      try {
        const resp = await getSimpleProgramList()
        if (resp && Array.isArray(resp.data)) {
          setProgramOptions(resp.data)
        } else if (Array.isArray(resp)) {
          setProgramOptions(resp)
        }
      } catch (e) {
        // ignore
      }
    }
    loadPrograms()
  }, [])

  const openDialog = async (expertId = null) => {
    if (!expertId) {
      setValues(defaultValues)
      setOpen(true)
      return
    }
    setLoading(true)
    try {
      const resp = await getExpertDetail(expertId)
      // Expecting { expert, programIds, innerciseIds }
      const e = resp.expert || resp.data?.expert || {}
      const programIds = resp.programIds || resp.data?.programIds || []
      const innerciseIds = resp.innerciseIds || resp.data?.innerciseIds || []
      const social = e.socialHandles || {};
      // Normalize twitter -> x for display
      const normalizedSocial = { ...defaultValues.socialHandles, ...social };
      if (normalizedSocial.twitter && !normalizedSocial.x) {
        normalizedSocial.x = normalizedSocial.twitter;
      }
      delete normalizedSocial.twitter;

      setValues({
        id: e.id,
        name: e.name || '',
        description: e.description || '',
        imageUrl: e.imageUrl || '',
        imageResourceId: e.imageResourceId || e.image?.id || null,
        link: e.link || '',
        isActive: !!e.isActive,
        sortOrder: e.sortOrder ?? 0,
        socialHandles: normalizedSocial,
        programIds,
        innerciseIds,
      })
      setOpen(true)
    } catch (err) {
      enqueueSnackbar('Failed to load expert details', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleMediaSelect = (selectedMedia) => {
    if (!selectedMedia) return
    const nextUrl =
      selectedMedia.urlDto?.imgUrl ||
      selectedMedia.urlDto?.originalUrl ||
      selectedMedia.url ||
      ''
    if (!nextUrl) return
    const resourceId = selectedMedia.id || selectedMedia.resourceId || null
    setValues((v) => ({
      ...v,
      imageUrl: nextUrl,
      imageResourceId: resourceId,
    }))
    enqueueSnackbar("Image selected successfully", { variant: "success" })
  }

  const onSave = async () => {
    setLoading(true)
    try {
      // Normalize social handles: ensure 'twitter' is removed and 'x' used
      const social = { ...(values.socialHandles || {}) }
      if (social.twitter && !social.x) {
        social.x = social.twitter
      }
      delete social.twitter

      const payload = {
        name: values.name,
        description: values.description,
        imageUrl: values.imageUrl || null,
        imageResourceId: values.imageUrl
          ? (values.imageResourceId ? Number(values.imageResourceId) : null)
          : null,
        link: values.link,
        socialHandles: social,
        isActive: values.isActive,
        sortOrder: Number(values.sortOrder) || 0,
        programIds: values.programIds,
        innerciseIds: values.innerciseIds,
      }
      if (values.id) {
        await updateExpert(values.id, payload)
        enqueueSnackbar('Expert updated', { variant: 'success' })
      } else {
        await createExpert(payload)
        enqueueSnackbar('Expert created', { variant: 'success' })
      }
      setOpen(false)
      onSubmit && onSubmit()
    } catch (e) {
      enqueueSnackbar('Save failed', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const searchInnercise = async () => {
    if (!innerciseSearch.trim()) {
      setInnerciseResults([])
      return
    }
    try {
      const resp = await queryInnerciseByTitle({ 
        pageNo: 1,
        pageSize: 1000,
        title: innerciseSearch.trim()
      })
      const list = resp?.data?.list || resp?.list || resp?.data || resp || []
      setInnerciseResults(Array.isArray(list) ? list : [])
    } catch (e) {
      console.error("Error searching innercises:", e)
      enqueueSnackbar("Failed to search innercises", { variant: "error" })
      setInnerciseResults([])
    }
  }

  const searchProgram = async () => {
    if (!programSearch.trim()) {
      setProgramResults([])
      return
    }
    try {
      const resp = await getPrograms({
        pageNo: 1,
        pageSize: 1000,
        title: programSearch.trim(),
        innerciseType: 1,
        pubStatus: 1,
        hasCategory: 1,
      })
      const list = resp?.data?.data?.list || resp?.data?.list || resp?.list || resp?.data || resp || []
      setProgramResults(Array.isArray(list) ? list : [])
    } catch (e) {
      console.error("Error searching programs:", e)
      enqueueSnackbar("Failed to search programs", { variant: "error" })
      setProgramResults([])
    }
  }

  const dialog = (
    <StyledExpertDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div className="dialog-title-group">
          <h2 className="dialog-title">
            {values.id ? "Edit Expert" : "Add New Expert"}
          </h2>
          <p className="dialog-subtitle">
            Manage profile details, social links, and related content assignments.
          </p>
        </div>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ paddingTop: '8px' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Name"
                value={values.name}
                onChange={(e) => setValues({ ...values, name: e.target.value })}
                fullWidth
                required
                error={!values.name && loading}
                helperText={!values.name && loading ? "Name is required" : ""}
                placeholder="e.g., John Doe"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Link"
                value={values.link}
                onChange={(e) => setValues({ ...values, link: e.target.value })}
                fullWidth
                placeholder="e.g., https://example.com"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={values.description}
                onChange={(e) => setValues({ ...values, description: e.target.value })}
                fullWidth
                multiline
                rows={3}
                placeholder="Brief description of the expert"
              />
            </Grid>
            
            {/* Image Section */}
            <Grid item xs={12}>
              <Typography className="section-label">
                Expert Image
              </Typography>
              <Box mb={2}>
                <TextField
                  label="Selected Image URL"
                  value={values.imageUrl}
                  onChange={(e) => setValues({
                    ...values,
                    imageUrl: e.target.value,
                    // Manual URL edits invalidate the prior media-library resource link
                    imageResourceId: null,
                  })}
                  fullWidth
                  placeholder="No image selected"
                  helperText="Image URL will be set automatically when you select media"
                  InputProps={{
                    readOnly: false,
                  }}
                />
                <Box mt={1}>
                  <Button
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => openMediaManager(handleMediaSelect, ['image'], 'search')}
                  >
                    Select Image from Media Library
                  </Button>
                </Box>
              </Box>
              
              {/* Image Preview */}
              {values.imageUrl && (
                <Box mt={2} display="flex" justifyContent="center">
                  <img
                    src={values.imageUrl}
                    alt="Expert Preview"
                    style={{
                      width: '100px',
                      height: '100px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '2px solid var(--cms-border)',
                      backgroundColor: 'var(--cms-surface-muted)'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none'
                    }}
                  />
                </Box>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Sort Order"
                type="number"
                value={values.sortOrder}
                onChange={(e) => setValues({ ...values, sortOrder: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={values.isActive}
                    onChange={(e) => setValues({ ...values, isActive: e.target.checked })}
                    color="primary"
                  />
                }
                label="Active"
              />
            </Grid>

            {/* Social Handles Section */}
            <Grid item xs={12}>
              <Typography className="section-label">
                Social Handles
              </Typography>
              <Grid container spacing={2}>
                {Object.keys(values.socialHandles || {}).map((key) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <TextField
                      label={key === 'x' ? 'X (Twitter)' : key.charAt(0).toUpperCase() + key.slice(1)}
                      value={values.socialHandles[key] || ''}
                      onChange={(e) => setValues({ 
                        ...values, 
                        socialHandles: { ...values.socialHandles, [key]: e.target.value } 
                      })}
                      fullWidth
                      placeholder={`https://${key}.com/username`}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* Related Programs Section */}
            <Grid item xs={12}>
              <Typography className="section-label">
                Related Programs
              </Typography>
              <Box mb={2}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={8}>
                    <TextField
                      label="Search by title"
                      value={programSearch}
                      onChange={(e) => setProgramSearch(e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Button 
                      onClick={searchProgram} 
                      variant="outlined" 
                      fullWidth
                      disabled={!programSearch.trim()}
                    >
                      Search
                    </Button>
                  </Grid>
                </Grid>
              </Box>
              {programResults.length > 0 && (
                <Box mb={2}>
                  <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                    Search Results:
                  </Typography>
                  <div className="selection-grid">
                    {programResults.map((p) => {
                      const isSelected = values.programIds.includes(p.id)
                      return (
                        <Chip
                          key={p.id}
                          label={p.title || p.name || `Program #${p.id}`}
                          onClick={() => {
                            const next = isSelected
                              ? values.programIds.filter((id) => id !== p.id)
                              : [...values.programIds, p.id]
                            setValues({ ...values, programIds: next })
                          }}
                          color={isSelected ? "primary" : "default"}
                          variant={isSelected ? "filled" : "outlined"}
                          style={{ cursor: "pointer" }}
                        />
                      )
                    })}
                  </div>
                </Box>
              )}
              {values.programIds.length > 0 && (
                <Box>
                  <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                    Selected Programs:
                  </Typography>
                  <div className="selection-grid">
                    {values.programIds.map((id) => {
                      const program = programResults.find(p => p.id === id) || 
                                     programOptions.find(p => p.id === id) ||
                                     { id, title: `Program #${id}` }
                      return (
                        <Chip
                          key={id}
                          label={program.title || program.name || `Program #${id}`}
                          onDelete={() => {
                            const next = values.programIds.filter((pid) => pid !== id)
                            setValues({ ...values, programIds: next })
                          }}
                          color="primary"
                          variant="filled"
                        />
                      )
                    })}
                  </div>
                </Box>
              )}
            </Grid>

            {/* Related Innercises Section */}
            <Grid item xs={12}>
              <Typography className="section-label">
                Related Innercises
              </Typography>
              <Box mb={2}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={8}>
                    <TextField
                      label="Search by title"
                      value={innerciseSearch}
                      onChange={(e) => setInnerciseSearch(e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Button 
                      onClick={searchInnercise} 
                      variant="outlined" 
                      fullWidth
                      disabled={!innerciseSearch.trim()}
                    >
                      Search
                    </Button>
                  </Grid>
                </Grid>
              </Box>
              {innerciseResults.length > 0 && (
                <Box mb={2}>
                  <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                    Search Results:
                  </Typography>
                  <div className="selection-grid">
                    {innerciseResults.map((i) => {
                      const isSelected = values.innerciseIds.includes(i.id)
                      return (
                        <Chip
                          key={i.id}
                          label={i.title || `Innercise #${i.id}`}
                          onClick={() => {
                            const exists = values.innerciseIds.includes(i.id)
                            const next = exists 
                              ? values.innerciseIds.filter((id) => id !== i.id) 
                              : [...values.innerciseIds, i.id]
                            setValues({ ...values, innerciseIds: next })
                          }}
                          color={isSelected ? "primary" : "default"}
                          variant={isSelected ? "filled" : "outlined"}
                          style={{ cursor: "pointer" }}
                        />
                      )
                    })}
                  </div>
                </Box>
              )}
              {values.innerciseIds.length > 0 && (
                <Box>
                  <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                    Selected Innercises:
                  </Typography>
                  <div className="selection-grid">
                    {values.innerciseIds.map((id) => {
                      const innercise = innerciseResults.find(i => i.id === id) || 
                                      { id, title: `Innercise #${id}` }
                      return (
                        <Chip
                          key={id}
                          label={innercise.title}
                          onDelete={() => {
                            const next = values.innerciseIds.filter((iid) => iid !== id)
                            setValues({ ...values, innerciseIds: next })
                          }}
                          color="primary"
                          variant="filled"
                        />
                      )
                    })}
                  </div>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          className="dialog-cancel-btn"
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          size="medium"
        >
          Cancel
        </Button>
        <Button
          className="dialog-submit-btn"
          onClick={onSave}
          variant="contained"
          size="medium"
          disabled={loading || !values.name}
        >
          {loading ? "Saving..." : values.id ? "Update" : "Create"}
        </Button>
      </DialogActions>
      
      {/* Media Manager Dialog */}
      {MediaManagerContent}
    </StyledExpertDialog>
  )

  return { openDialog, dialog }
}

export default useExpertDialog


